/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { observer } from 'mobx-react';
import { ActionableNotification, Stack } from '@carbon/react';
import { useEffect, useState } from 'react';

import PlayPerformanceNotification from 'App/Pages/Diagram/Deployment/PlayPerformanceNotification';
import PlayBillingNotification from 'App/Pages/Diagram/Deployment/PlayBillingNotification';
import { isExecuteMode, isPlayMode } from 'App/Pages/Diagram/Deployment/executeModal/modeUtils';
import { parseClusterZeebeVersion } from 'utils/cluster/cluster-version-util';
import { ClustersList } from 'components';
import { processApplicationStore, currentDiagramStore } from 'stores';
import { deploymentStore } from 'App/Pages/Diagram/Deployment/stores';
import ContainedResourcesNotification from 'App/Pages/Diagram/Deployment/executeModal/ContainedResourcesNotification';
import { AddVariable } from 'App/Pages/Diagram/Deployment/executeModal/AddVariable';
import DeployLinkedDiagramInfo from 'App/Pages/Diagram/Deployment/executeModal/DeployLinkedDiagramInfo';
import getClusterStatus from 'utils/cluster/get-cluster-status';
import { CLUSTER_STATUS } from 'utils/cluster/constants';
import { DeploymentPolicyWarning, DEPLOYMENT_POLICY_CHECK_RESULT } from 'features/deploy-to-prod-policy';

import ClusterHeader from './ClusterHeader';
import PublicationClusterMessage from './PublicationClusterMessage';
import * as Styled from './ClusterSelection.styled';
import HealthyClusterRequiredNotification from './HealthyClusterRequiredNotification';
import ClusterPausedResumingNotification from './ClusterPausedResumingNotification';
import SecuredConnectorsMessage from './SecuredConnectorsMessage/SecuredConnectorsMessage';
import MissingClientsMessage from './MissingClientsMessage/MissingClientsMessage';

export const ClusterSelection = ({
  clusters,
  mode,
  onClose,
  payload,
  setPayload,
  isValidPayload,
  disabled,
  componentsForHealthCheck,
  deploymentPolicyCheckResult
}) => {
  const { executionPlatformVersion, isBPMN, state, modeler } = currentDiagramStore;
  const { diagram } = state;
  const { selectedClusterId, setSelectedClusterId, processApplicationVersionId } = deploymentStore;
  const { processApplication, fromAProcessApplication } = processApplicationStore;
  const isAllowedToDeployToSelectedCluster =
    deploymentPolicyCheckResult === DEPLOYMENT_POLICY_CHECK_RESULT.NO_RESTRICTIONS;

  const version = { deploy: null, lint: null };
  let selectedCluster;
  if (clusters?.length > 0) {
    selectedCluster = clusters.find((cluster) => cluster.uuid === selectedClusterId);
    if (selectedCluster?.generation?.versions?.zeebe) {
      const selectedClusterVersionInfo = parseClusterZeebeVersion(selectedCluster.generation?.versions?.zeebe);
      if (selectedClusterVersionInfo) {
        const { major, minor } = selectedClusterVersionInfo;

        version['deploy'] = `${major}.${minor}`;
        version['lint'] = `${executionPlatformVersion}`.substring(0, 3);
      }
    }
  }

  const [diagramXml, setDiagramXml] = useState(null);

  useEffect(() => {
    (async () => {
      if (!modeler) {
        return;
      }

      const { xml } = await modeler.saveXML();
      if (xml) {
        setDiagramXml(xml);
      }
    })();
  }, [modeler]);

  const isOnlyOneClusterPresentAndItsBeingCreated =
    clusters?.length === 1 && selectedCluster?.status?.zeebe === CLUSTER_STATUS.CREATING;

  const inAProcessApplicationWithNoStagesClusters =
    processApplicationStore.fromAProcessApplication && !clusters?.length;

  const clusterStatus = getClusterStatus(selectedCluster?.status, componentsForHealthCheck);
  return (
    <>
      <ClusterHeader
        mode={mode}
        onClose={onClose}
        isClusterBeingCreated={clusters?.length === 1 && clusters[0].status?.zeebe === CLUSTER_STATUS.CREATING}
        disabled={disabled}
      />

      {inAProcessApplicationWithNoStagesClusters ? (
        <Styled.NoStagesClustersNotification
          inline
          actionButtonLabel={'Learn more'}
          onActionButtonClick={() => {
            /*TODO Update the learn more link: https://github.com/camunda/web-modeler/issues/10299*/
            return window.open(
              'https://docs.camunda.io/docs/next/components/modeler/web-modeler/process-applications/'
            );
          }}
          kind="warning"
          lowContrast
          hideCloseButton
          title="Undefined stages"
          subtitle="To be able to deploy you have to define clusters for your stages."
        />
      ) : (
        <ClustersList
          clusters={clusters}
          disabled={disabled}
          selectedClusterId={selectedClusterId}
          onClusterIdSelection={(clusterId) => setSelectedClusterId(clusterId)}
          componentsForHealthCheck={componentsForHealthCheck}
        />
      )}

      <Styled.InfosContainer>
        {!isAllowedToDeployToSelectedCluster ? (
          <DeploymentPolicyWarning
            deploymentPolicyCheckResult={deploymentPolicyCheckResult}
            processApplicationId={processApplication?.id}
            isDraft={!processApplicationVersionId}
            closeModal={onClose}
          />
        ) : [CLUSTER_STATUS.RESUMING, CLUSTER_STATUS.SUSPENDED].includes(clusterStatus) ? (
          <ClusterPausedResumingNotification clusterId={selectedClusterId} status={clusterStatus} />
        ) : clusterStatus === CLUSTER_STATUS.HEALTHY ? (
          <>
            <Stack gap={3}>
              {isPlayMode(mode) && selectedCluster?.playMode === false && (
                // @ts-expect-error TS2739
                <PlayPerformanceNotification
                  onClose={onClose}
                  shouldLinkToConsole={!fromAProcessApplication}
                  selectedClusterId={selectedClusterId}
                />
              )}
              {isPlayMode(mode) && (
                // @ts-expect-error TS2741
                <PlayBillingNotification onClose={onClose} shouldOpenSettings={fromAProcessApplication} />
              )}
            </Stack>

            {!isPlayMode(mode) && (
              <>
                {fromAProcessApplication && processApplication.files && (
                  <ContainedResourcesNotification resources={processApplication.files.length} />
                )}
                <DeployLinkedDiagramInfo />
                {isBPMN && (
                  <Styled.EngineSwitchInfo>
                    {version['lint'] !== version['deploy'] && (
                      <ActionableNotification
                        inline
                        /**
                         * This prop is deprecated and will be removed in the next major version v12 https://react.carbondesignsystem.com/iframe.html?viewMode=docs&id=components-notifications-actionable--overview&globals=#component-api
                         * But we have to still use it here to prevent focus trap, which is preventing users from selecting a cluster for deployment.
                         *
                         * Alternative is to replace ActionableNotifiation with Callout component which is currently unstable. https://react.carbondesignsystem.com/iframe.html?viewMode=docs&id=components-notifications-actionable--overview&globals=#unstable__callout-previously-staticnotification
                         * Hopefully it becomes stable with v12 and we can replace with Callout with the upgrade to v12.
                         */
                        hasFocus={false}
                        actionButtonLabel={'Learn more'}
                        onActionButtonClick={() =>
                          window.open(
                            'https://docs.camunda.io/docs/components/modeler/web-modeler/fix-problems-in-your-diagram/#engine-version-selection'
                          )
                        }
                        kind="info"
                        lowContrast
                        hideCloseButton
                        title="Engine version mismatch"
                        subtitle={`If you deploy to the selected cluster, your diagram will be checked for problems against Zeebe ${version['deploy']} going forward`}
                      />
                    )}
                  </Styled.EngineSwitchInfo>
                )}
              </>
            )}
          </>
        ) : (
          clusters?.length > 0 &&
          !isOnlyOneClusterPresentAndItsBeingCreated &&
          selectedCluster?.status?.zeebe &&
          selectedCluster?.status?.zeebe !== CLUSTER_STATUS.HEALTHY && (
            <HealthyClusterRequiredNotification mode={mode} />
          )
        )}

        {mode !== 'play' && (
          <>
            {isBPMN && <PublicationClusterMessage clusters={clusters} selectedCluster={selectedCluster} />}

            <SecuredConnectorsMessage />

            <MissingClientsMessage diagramXml={diagramXml} diagramType={diagram.type} />
          </>
        )}
      </Styled.InfosContainer>

      {clusters?.length > 0 && isExecuteMode(mode) && (
        <AddVariable payload={payload} setPayload={setPayload} isValidPayload={isValidPayload} />
      )}
    </>
  );
};

export default observer(ClusterSelection);
